import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import OutgoingPrintModal from "components/OutgoingPrintModal";
import ReceiverDataOutdatedBadge from "components/ReceiverDataOutdatedBadge";
import RefreshReceiverDataModal from "components/RefreshReceiverDataModal";

import dispatchChartService from "api/dispatchChartService";
import {
  EParcelDeliveryCategory,
  IDispatchChart,
  IDispatchListItem,
  IOutgoingCategory,
  IPendingReceptionItem,
  IPuhSentItem,
} from "dto/Mailroom/IDispatchChart";

import { ENotificationType, notification } from "utils/notification";

import CreateDispatchChartModal from "./CreateDispatchChartModal";
import ChangeDeliveryKindModal from "./ChangeDeliveryKindModal";
import GroupParcelsModal from "./GroupParcelsModal";
import OutgoingRowMenu from "./OutgoingRowMenu";
import ProcessDispatchChartModal from "./ProcessDispatchChartModal";
import ReceptionModal from "./ReceptionModal";
import ParcelPreviewModal from "./ParcelPreviewModal";
import styles from "./outgoing.module.scss";

type SubView = "dispatch" | "charts" | "reception";

// Skrzynka „Wysłane PUH" (port listy RegistryOfficeOutgoingPUHSent z Magistratu) — zakładka
// poza kategoriami form doręczenia: hybrydowe są nadawane elektronicznie przez kolejkę, a tu
// biuro widzi, co Poczta wydrukowała/nadała w danym miesiącu i jaki ma status doręczenia.
const PUH_SENT_TAB = -1;

// Kategorie fizyczne, dla których ma sens koperta wspólna (Magistrat: SupportsGroupingParcels
// dla poczty / kuriera / doręczenia osobistego; elektroniczne nie mają „koperty").
const GROUPING_CATEGORIES = new Set<number>([
  EParcelDeliveryCategory.Post,
  EParcelDeliveryCategory.PersonalPickup,
  EParcelDeliveryCategory.PersonalDelivery,
  EParcelDeliveryCategory.Courier,
]);

const SUBVIEW_LABELS: Record<SubView, string> = {
  dispatch: "Do wysyłki",
  charts: "Karty nadawcze",
  reception: "Zwrotki ZPO",
};

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString("pl-PL") : "—";

// Escape dla treści wstrzykiwanej do HTML wydruku (nazwy/adresy z danych).
const esc = (v?: string | null) =>
  (v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const fmtPln = (v?: number | null) =>
  v == null ? "" : `${v.toFixed(2).replace(".", ",")} zł`;

interface Props {
  searchText: string;
}

const OutgoingView = ({ searchText }: Props) => {
  const queryClient = useQueryClient();

  const { data: catData } = dispatchChartService.useGetOutgoingCategories();
  const categories: IOutgoingCategory[] = useMemo(
    () => catData?.data?.Categories ?? [],
    [catData]
  );
  const puhSentCount = catData?.data?.PuhSentCount ?? 0;

  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [subView, setSubView] = useState<SubView>("dispatch");
  const [postGroup, setPostGroup] = useState<number | null>(null);

  const activeCategory = useMemo(
    () => categories.find((c) => c.Category === activeCategoryId) ?? null,
    [categories, activeCategoryId]
  );

  // Domyślna kategoria = pierwsza z przesyłkami, inaczej pierwsza dostępna.
  useEffect(() => {
    if (activeCategoryId !== null || categories.length === 0) return;
    const withItems = categories.find(
      (c) => c.ReadyToDispatchCount + c.ChartCount + c.PendingReceptionCount > 0
    );
    setActiveCategoryId((withItems ?? categories[0]).Category);
  }, [categories, activeCategoryId]);

  // Podgrupa Poczty — domyślnie pierwsza.
  useEffect(() => {
    if (activeCategory?.Category === EParcelDeliveryCategory.Post) {
      setPostGroup(
        (prev) => prev ?? activeCategory.PostGroups[0]?.PostGroup ?? null
      );
    } else {
      setPostGroup(null);
    }
  }, [activeCategory]);

  // Gdy zakładka nie wspiera bieżącego podwidoku — wróć na „Do wysyłki".
  useEffect(() => {
    if (!activeCategory) return;
    if (subView === "charts" && !activeCategory.SupportsDispatchChart)
      setSubView("dispatch");
    if (
      subView === "reception" &&
      !activeCategory.SupportsReceptionConfirmation
    )
      setSubView("dispatch");
  }, [activeCategory, subView]);

  const invalidate = () => {
    queryClient.invalidateQueries(["outgoing.categories"]);
    queryClient.invalidateQueries(["outgoing.dispatchList"]);
    queryClient.invalidateQueries(["outgoing.charts"]);
    queryClient.invalidateQueries(["outgoing.pendingReception"]);
    queryClient.invalidateQueries(["outgoing.puhSent"]);
  };

  const isEpuap = activeCategory?.Category === EParcelDeliveryCategory.Epuap;
  const isPuhSent = activeCategoryId === PUH_SENT_TAB;

  return (
    <div className={styles.wrap}>
      {/* Zakładki kategorii */}
      <div className={styles.categoryTabs}>
        {categories.map((c) => {
          const total =
            c.ReadyToDispatchCount + c.ChartCount + c.PendingReceptionCount;
          return (
            <button
              key={c.Category}
              className={`${styles.catTab} ${
                c.Category === activeCategoryId ? styles.catTabActive : ""
              }`}
              onClick={() => {
                setActiveCategoryId(c.Category);
                setSubView("dispatch");
              }}
            >
              {c.Name}
              {total > 0 && <span className={styles.badge}>{total}</span>}
            </button>
          );
        })}
        {categories.length > 0 && (
          <button
            className={`${styles.catTab} ${styles.catTabAside} ${
              isPuhSent ? styles.catTabActive : ""
            }`}
            onClick={() => {
              setActiveCategoryId(PUH_SENT_TAB);
              setSubView("dispatch");
            }}
            title="Przesyłki hybrydowe (PUH) nadane elektronicznie — wydruk i doręczenie przez Pocztę Polską"
          >
            Wysłane PUH
            {puhSentCount > 0 && (
              <span className={styles.badge}>{puhSentCount}</span>
            )}
          </button>
        )}
      </div>

      {isPuhSent ? (
        <PuhSentList searchText={searchText} />
      ) : isEpuap ? (
        <div className={styles.placeholder}>
          Integracja ePUAP nie jest jeszcze dostępna w Nocie. Zakładka
          przygotowana pod przyszłe wdrożenie.
        </div>
      ) : activeCategory ? (
        <>
          {/* Przełącznik podwidoków (zależny od możliwości kategorii) */}
          <div className={styles.subTabs}>
            {(["dispatch", "charts", "reception"] as SubView[])
              .filter(
                (sv) =>
                  sv === "dispatch" ||
                  (sv === "charts" && activeCategory.SupportsDispatchChart) ||
                  (sv === "reception" &&
                    activeCategory.SupportsReceptionConfirmation)
              )
              .map((sv) => (
                <button
                  key={sv}
                  className={`${styles.subTab} ${
                    subView === sv ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubView(sv)}
                >
                  {SUBVIEW_LABELS[sv]}
                </button>
              ))}
          </div>

          {/* Wewnętrzny przełącznik Poczty (polecone/ZPO vs zwykłe) */}
          {subView === "dispatch" &&
            activeCategory.Category === EParcelDeliveryCategory.Post &&
            activeCategory.PostGroups.length > 1 && (
              <div className={styles.postGroups}>
                {activeCategory.PostGroups.map((pg) => (
                  <button
                    key={pg.PostGroup}
                    className={`${styles.pgBtn} ${
                      postGroup === pg.PostGroup ? styles.pgBtnActive : ""
                    }`}
                    onClick={() => setPostGroup(pg.PostGroup)}
                  >
                    {pg.Name}
                    {pg.Count > 0 && (
                      <span className={styles.badgeSm}>{pg.Count}</span>
                    )}
                  </button>
                ))}
              </div>
            )}

          <div className={styles.content}>
            {subView === "dispatch" && (
              <DispatchList
                category={activeCategory}
                postGroup={postGroup}
                searchText={searchText}
                onChanged={invalidate}
              />
            )}
            {subView === "charts" && (
              <ChartsList
                category={activeCategory}
                searchText={searchText}
                onChanged={invalidate}
              />
            )}
            {subView === "reception" && (
              <ReceptionList
                category={activeCategory}
                searchText={searchText}
                onChanged={invalidate}
              />
            )}
          </div>
        </>
      ) : (
        <div className={styles.placeholder}>Brak kategorii wychodzących.</div>
      )}
    </div>
  );
};

// ── Podwidok „Do wysyłki" (wielowybór → utwórz kartę nadawczą) ────────────────

const DispatchList = ({
  category,
  postGroup,
  searchText,
  onChanged,
}: {
  category: IOutgoingCategory;
  postGroup: number | null;
  searchText: string;
  onChanged: () => void;
}) => {
  const { data, isLoading } = dispatchChartService.useGetDispatchList({
    Category: category.Category,
    PostGroup: postGroup,
  });
  const { t } = useTranslation();
  const { mutate: ungroup } = dispatchChartService.useUngroupParcels();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showGroup, setShowGroup] = useState(false);
  // „Aktualizuj dane adresata” — przesyłki do przepięcia na bieżące dane z książki adresowej.
  const [refreshIds, setRefreshIds] = useState<string[] | null>(null);
  const [changeKind, setChangeKind] = useState<IDispatchListItem[] | null>(
    null
  );
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);
  const [printing, setPrinting] = useState<{
    ids: string[];
    title: string;
  } | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const items: IDispatchListItem[] = useMemo(() => {
    const all = data?.data?.Items ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    const matches = (x: IDispatchListItem) =>
      [x.ReceiverName, x.Title, x.ParcelNumber, x.ReceiverAddress, x.AuthorName]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q));
    // Koperta pasuje też, gdy pasuje któreś ze zgrupowanych pism.
    return all.filter(
      (x) => matches(x) || (x.GroupedParcels ?? []).some(matches)
    );
  }, [data, searchText]);

  // Wyczyść selekcję gdy zmienia się lista.
  useEffect(() => setSelected(new Set()), [category.Category, postGroup]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const doUngroup = (item: IDispatchListItem) =>
    ungroup(
      { ParcelId: item.Id },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Rozgrupowano kopertę",
            text: "Pisma wróciły na listę jako samodzielne przesyłki.",
          });
          onChanged();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Nie udało się rozgrupować",
            text:
              (e as { response?: { data?: { Message?: string } } })?.response
                ?.data?.Message ?? "Spróbuj ponownie po odświeżeniu listy.",
          }),
      }
    );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedItems = items.filter((x) => selected.has(x.Id));
  // Karta nadawcza = jedna forma doręczenia.
  const kinds = new Set(selectedItems.map((x) => x.DeliveryKindId));
  const mixedKinds = kinds.size > 1;
  const canCreate =
    category.SupportsDispatchChart && selectedItems.length > 0 && !mixedKinds;
  // Koperta wspólna: ≥2 samodzielne przesyłki fizycznej kategorii (koperta w kopercie — nie).
  const canGroup =
    GROUPING_CATEGORIES.has(category.Category) &&
    selectedItems.length >= 2 &&
    selectedItems.every((x) => !x.IsGroupingParcel);
  const outdatedSelected = selectedItems.filter((x) => x.ReceiverDataOutdated);

  const renderRow = (it: IDispatchListItem, child = false) => (
    <label
      key={it.Id}
      className={`${styles.row} ${child ? styles.rowChild : ""} ${
        it.IsGroupingParcel ? styles.rowGrouping : ""
      }`}
      title="Dwuklik — podgląd pisma"
      onDoubleClick={() => setPreview({ id: it.Id, title: it.Title })}
    >
      {child ? (
        <span className={styles.childIndent} />
      ) : (
        <input
          type="checkbox"
          checked={selected.has(it.Id)}
          onChange={() => toggle(it.Id)}
        />
      )}
      {it.IsGroupingParcel && (
        <button
          type="button"
          className={styles.expandBtn}
          title={expanded.has(it.Id) ? "Zwiń pisma" : "Rozwiń pisma"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleExpanded(it.Id);
          }}
        >
          <FontAwesomeIcon
            icon={expanded.has(it.Id) ? faChevronDown : faChevronRight}
          />
        </button>
      )}
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          {it.ReceiverName ?? "—"}
          {it.ParcelNumber && (
            <span className={styles.sig}>{it.ParcelNumber}</span>
          )}
          {it.IsGroupingParcel && (
            <span className={styles.groupBadge}>
              koperta wspólna · {it.GroupedParcels?.length ?? 0} pism
            </span>
          )}
          {it.ReceiverDataOutdated && <ReceiverDataOutdatedBadge />}
        </div>
        <div className={styles.rowSub}>
          {it.Title ?? ""}
          {it.ReceiverAddress ? ` · ${it.ReceiverAddress}` : ""}
        </div>
      </div>
      <div className={styles.rowMeta}>
        <div>{it.DeliveryKindName}</div>
        {isTeamView && it.AuthorName && (
          <div className={styles.author}>{it.AuthorName}</div>
        )}
      </div>
      {child ? (
        <span className={styles.rowMenuPlaceholder} />
      ) : (
        <OutgoingRowMenu
          item={it}
          onChangeKind={(x) => setChangeKind([x])}
          onUngroup={doUngroup}
          onRefreshReceiverData={(x) => setRefreshIds([x.Id])}
          onPrint={(x) =>
            setPrinting({
              ids: [x.Id],
              title: [x.ReceiverName, x.ParcelNumber].filter(Boolean).join(" · ") || "Przesyłka",
            })
          }
        />
      )}
    </label>
  );

  return (
    <>
      <div className={styles.listToolbar}>
        <div className={styles.toolbarInfo}>
          {selected.size > 0
            ? `Zaznaczono: ${selected.size}`
            : `Przesyłek: ${items.length}`}
          {mixedKinds && (
            <span className={styles.warn}>
              Do jednej karty wybierz przesyłki tej samej formy.
            </span>
          )}
        </div>
        <div className={styles.toolbarActions}>
          {selectedItems.length > 0 && (
            <HollowButton
              text={`Drukuj (${selectedItems.length})`}
              onClick={() =>
                setPrinting({
                  ids: selectedItems.map((x) => x.Id),
                  title: `Zaznaczone przesyłki (${selectedItems.length})`,
                })
              }
            />
          )}
          {selectedItems.length > 0 && (
            <HollowButton
              text="Zmień sposób nadania"
              onClick={() => setChangeKind(selectedItems)}
            />
          )}
          {outdatedSelected.length > 0 && (
            <HollowButton
              text={t("receiverData.refreshSelected", { count: outdatedSelected.length })}
              onClick={() => setRefreshIds(outdatedSelected.map((x) => x.Id))}
            />
          )}
          {GROUPING_CATEGORIES.has(category.Category) &&
            selectedItems.length > 0 && (
              <HollowButton
                text="Przesyłka grupująca"
                onClick={() => setShowGroup(true)}
                disabled={!canGroup}
              />
            )}
          {category.SupportsDispatchChart && (
            <Button
              text="Utwórz kartę nadawczą"
              onClick={() => setShowCreate(true)}
              disabled={!canCreate}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <div className={styles.empty}>Ładowanie…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>Brak przesyłek do wysyłki.</div>
      ) : (
        <div className={styles.table}>
          {items.map((it) => (
            <div key={it.Id} className={styles.rowGroup}>
              {renderRow(it)}
              {it.IsGroupingParcel &&
                expanded.has(it.Id) &&
                (it.GroupedParcels ?? []).map((c) => renderRow(c, true))}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateDispatchChartModal
          parcels={selectedItems}
          category={category}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            setSelected(new Set());
            onChanged();
          }}
        />
      )}

      {showGroup && (
        <GroupParcelsModal
          parcels={selectedItems}
          onClose={() => setShowGroup(false)}
          onDone={() => {
            setShowGroup(false);
            setSelected(new Set());
            onChanged();
          }}
        />
      )}

      {changeKind && (
        <ChangeDeliveryKindModal
          items={changeKind}
          onClose={() => setChangeKind(null)}
          onDone={() => {
            setChangeKind(null);
            setSelected(new Set());
            onChanged();
          }}
        />
      )}

      {refreshIds && (
        <RefreshReceiverDataModal
          parcelIds={refreshIds}
          onClose={() => setRefreshIds(null)}
          onDone={() => {
            setSelected(new Set());
            onChanged();
          }}
        />
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}

      {printing && (
        <OutgoingPrintModal
          source={{ ParcelIds: printing.ids }}
          context="mailroom"
          title={printing.title}
          onClose={() => setPrinting(null)}
        />
      )}
    </>
  );
};

// ── Podwidok „Karty nadawcze" ─────────────────────────────────────────────────

const ChartsList = ({
  category,
  searchText,
  onChanged,
}: {
  category: IOutgoingCategory;
  searchText: string;
  onChanged: () => void;
}) => {
  const { data, isLoading } = dispatchChartService.useGetDispatchCharts({
    Category: category.Category,
    IncludeDispatched: false,
  });
  const { mutate: removeChart } = dispatchChartService.useRemoveFromChart();
  const [processChart, setProcessChart] = useState<IDispatchChart | null>(null);
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);
  const [printChart, setPrintChart] = useState<IDispatchChart | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const charts: IDispatchChart[] = useMemo(() => {
    const all = data?.data?.Charts ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.Signature.toLowerCase().includes(q) ||
        (c.CreatorName ?? "").toLowerCase().includes(q) ||
        c.Parcels.some((p) => (p.ReceiverName ?? "").toLowerCase().includes(q))
    );
  }, [data, searchText]);

  // Wydruk karty nadawczej — odwzorowanie „pocztowej książki nadawczej" z Magistratu
  // (ParcelsPdfHelper.PrintDispatchChart): A4 poziomo, kolumny urzędowego druku, opłata
  // rozbita na zł/gr. Doręczenie osobiste → wariant instytucjonalny (bez opłat, Podpis/Data).
  const doPrint = (chart: IDispatchChart) => {
    const institutional = chart.ChartTypeCode === "DO";

    // „Dokładne miejsce doręczenia" = adres [+ sygnatura przesyłki] [+ tytuł].
    const place = (p: IDispatchChart["Parcels"][number]) =>
      [p.ReceiverAddress, p.ParcelNumber, p.Title]
        .filter(Boolean)
        .map((v) => esc(v))
        .join(", ");

    const fee = (v?: number | null) => {
      if (v == null) return { zl: "", gr: "" };
      const zl = Math.trunc(v);
      const gr = Math.round((v - zl) * 100);
      return { zl: String(zl), gr: String(gr).padStart(2, "0") };
    };

    const sender = `${esc(chart.SenderName)} (${esc(chart.Signature)})`;

    const rows = institutional
      ? chart.Parcels.map(
          (p, i) =>
            `<tr><td class="c">${i + 1}</td><td>${esc(
              p.ReceiverName
            )}</td><td>${place(p)}</td><td></td><td></td></tr>`
        ).join("")
      : chart.Parcels.map((p, i) => {
          const f = fee(p.Fee);
          return `<tr><td class="c">${i + 1}</td><td>${esc(
            p.ReceiverName
          )}</td><td>${place(
            p
          )}</td><td></td><td></td><td></td><td></td><td></td><td></td><td class="c">${
            f.zl
          }</td><td class="c">${f.gr}</td><td></td><td></td></tr>`;
        }).join("");

    const head = institutional
      ? `<tr class="s"><td colspan="2" class="sl">Imię i nazwisko (nazwa) oraz adres nadawcy</td><td colspan="3" class="sv">${sender}</td></tr>
         <tr><th style="width:8mm">Lp.</th><th>ADRESAT<br>(imię i nazwisko lub nazwa)</th><th>Dokładne miejsce doręczenia</th><th style="width:60mm">Podpis</th><th style="width:24mm">Data</th></tr>`
      : `<tr class="s"><td colspan="3" class="sl">Imię i nazwisko (nazwa) oraz adres nadawcy</td><td colspan="10" class="sv">${sender}</td></tr>
         <tr>
           <th rowspan="2" style="width:8mm">Lp.</th>
           <th rowspan="2">ADRESAT<br>(imię i nazwisko lub nazwa)</th>
           <th rowspan="2">Dokładne miejsce doręczenia</th>
           <th colspan="2">Kwota zadekl. wartości</th>
           <th colspan="2">Masa</th>
           <th rowspan="2" style="width:34mm">Nr nadawczy</th>
           <th rowspan="2" style="width:18mm">Uwagi</th>
           <th colspan="2">Opłata</th>
           <th colspan="2">Kwota pobrania</th>
         </tr>
         <tr>
           <th>zł</th><th>gr</th><th>kg</th><th>g</th><th>zł</th><th>gr</th><th>zł</th><th>gr</th>
         </tr>`;

    const html = `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>Karta nadawcza ${esc(
      chart.Signature
    )}</title><style>
      @page{size:A4 landscape;margin:12mm}
      body{font-family:Arial,Helvetica,sans-serif;color:#000;margin:0}
      table{width:100%;border-collapse:collapse;font-size:10px}
      th,td{border:1px solid #000;padding:3px 5px;vertical-align:top}
      th{background:#f0f0f0;font-size:9px;text-align:center;font-weight:700}
      td{height:12mm}
      td.c{text-align:center}
      tr.s td{border:none;height:auto;padding:2px 4px 8px}
      td.sl{font-weight:700;font-size:11px}
      td.sv{text-align:right;font-size:11px;font-weight:700}
      @media print{button{display:none}}
    </style></head><body>
      <table><thead>${head}</thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print()}</script>
    </body></html>`;

    const w = window.open("", "_blank");
    if (!w) {
      notification({
        type: ENotificationType.ERROR,
        title: "Nie udało się otworzyć wydruku",
        text: "Zezwól na wyskakujące okna dla tej strony.",
      });
      return;
    }
    w.document.write(html);
    w.document.close();
  };

  const doRemove = (chartId: string) =>
    removeChart(
      { ChartId: chartId },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Usunięto kartę",
            text: "Przesyłki wróciły na listę „Do wysyłki”.",
          });
          onChanged();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się usunąć karty.",
          }),
      }
    );

  if (isLoading) return <div className={styles.empty}>Ładowanie…</div>;
  if (charts.length === 0)
    return <div className={styles.empty}>Brak kart nadawczych.</div>;

  return (
    <>
      <div className={styles.charts}>
        {charts.map((c) => (
          <div key={c.Id} className={styles.chartCard}>
            <div className={styles.chartHead}>
              <div>
                <div className={styles.chartSig}>{c.Signature}</div>
                <div className={styles.chartMeta}>
                  {c.DeliveryKindName} · {c.ParcelCount} szt.
                  {(() => {
                    const total = c.Parcels.reduce(
                      (s, p) => s + (p.Fee ?? 0),
                      0
                    );
                    return total > 0 ? ` · opłata: ${fmtPln(total)}` : "";
                  })()}{" "}
                  · utworzono {fmtDate(c.CreationDate)}
                  {isTeamView && c.CreatorName
                    ? ` · autor: ${c.CreatorName}`
                    : ""}
                </div>
              </div>
              <div className={styles.chartActions}>
                <HollowButton text="Drukuj kartę" onClick={() => doPrint(c)} />
                <HollowButton
                  text="Drukuj przesyłki"
                  onClick={() => setPrintChart(c)}
                />
                <Button
                  text="Potwierdź wysyłkę"
                  onClick={() => setProcessChart(c)}
                />
                <HollowButton text="Usuń" onClick={() => doRemove(c.Id)} />
              </div>
            </div>
            <div className={styles.chartParcels}>
              {c.Parcels.map((p) => (
                <div
                  key={p.Id}
                  className={styles.chartParcel}
                  title="Dwuklik — podgląd pisma"
                  onDoubleClick={() => setPreview({ id: p.Id, title: p.Title })}
                >
                  <span className={styles.sig}>{p.ParcelNumber ?? "—"}</span>
                  <span>{p.ReceiverName ?? "—"}</span>
                  <span className={styles.dim}>{p.ReceiverAddress ?? ""}</span>
                  {p.IsGroupingParcel && (
                    <span className={styles.groupBadge}>
                      koperta · {p.GroupedParcels?.length ?? 0} pism
                    </span>
                  )}
                  {p.Fee != null && (
                    <span className={styles.fee}>{fmtPln(p.Fee)}</span>
                  )}
                  {isTeamView && p.AuthorName && (
                    <span className={styles.author}>{p.AuthorName}</span>
                  )}
                </div>
              ))}
              {c.Parcels.filter((p) => p.IsGroupingParcel).map((p) =>
                (p.GroupedParcels ?? []).map((g) => (
                  <div
                    key={g.Id}
                    className={`${styles.chartParcel} ${styles.chartParcelChild}`}
                    title="Pismo w kopercie wspólnej — dwuklik: podgląd"
                    onDoubleClick={() =>
                      setPreview({ id: g.Id, title: g.Title })
                    }
                  >
                    <span className={styles.childIndent} />
                    <span className={styles.sig}>{g.ParcelNumber ?? "—"}</span>
                    <span>{g.Title ?? g.ReceiverName ?? "—"}</span>
                    <span className={styles.dim}>
                      w kopercie {p.ParcelNumber ?? ""}
                    </span>
                    {isTeamView && g.AuthorName && (
                      <span className={styles.author}>{g.AuthorName}</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {processChart && (
        <ProcessDispatchChartModal
          chart={processChart}
          onClose={() => setProcessChart(null)}
          onDone={() => {
            setProcessChart(null);
            onChanged();
          }}
        />
      )}

      {printChart && (
        <OutgoingPrintModal
          source={{ ParcelIds: printChart.Parcels.map((p) => p.Id) }}
          context="mailroom"
          title={`Karta nadawcza ${printChart.Signature}`}
          onClose={() => setPrintChart(null)}
        />
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
};

// ── Podwidok „Zwrotki ZPO" ────────────────────────────────────────────────────

const ReceptionList = ({
  category,
  searchText,
  onChanged,
}: {
  category: IOutgoingCategory;
  searchText: string;
  onChanged: () => void;
}) => {
  const { data, isLoading } = dispatchChartService.useGetPendingReception({
    Category: category.Category,
  });
  const { mutate: resend } = dispatchChartService.useResendParcel();
  const [reception, setReception] = useState<IPendingReceptionItem | null>(
    null
  );
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const items: IPendingReceptionItem[] = useMemo(() => {
    const all = data?.data?.Items ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    return all.filter((x) =>
      [x.ReceiverName, x.Title, x.ParcelNumber, x.AuthorName]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q))
    );
  }, [data, searchText]);

  const doResend = (id: string) =>
    resend(
      { ParcelId: id },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Utworzono ponowną przesyłkę",
            text: "Nowa przesyłka trafiła na listę „Do wysyłki”.",
          });
          onChanged();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się utworzyć ponownej przesyłki.",
          }),
      }
    );

  if (isLoading) return <div className={styles.empty}>Ładowanie…</div>;
  if (items.length === 0)
    return (
      <div className={styles.empty}>
        Brak przesyłek ZPO oczekujących na potwierdzenie odbioru.
      </div>
    );

  return (
    <>
      <div className={styles.table}>
        {items.map((it) => (
          <div
            key={it.Id}
            className={styles.row}
            title="Dwuklik — podgląd pisma"
            onDoubleClick={() => setPreview({ id: it.Id, title: it.Title })}
          >
            <div className={styles.rowMain}>
              <div className={styles.rowTitle}>
                {it.ReceiverName ?? "—"}
                {it.ParcelNumber && (
                  <span className={styles.sig}>{it.ParcelNumber}</span>
                )}
              </div>
              <div className={styles.rowSub}>
                {it.Title ?? ""} · nadano {fmtDate(it.DispatchDate)}
                {isTeamView && it.AuthorName
                  ? ` · autor: ${it.AuthorName}`
                  : ""}
              </div>
            </div>
            <div className={styles.rowActions}>
              <Button
                text="Zarejestruj odbiór"
                onClick={() => setReception(it)}
              />
              <HollowButton
                text="Wyślij ponownie"
                onClick={() => doResend(it.Id)}
              />
            </div>
          </div>
        ))}
      </div>

      {reception && (
        <ReceptionModal
          item={reception}
          onClose={() => setReception(null)}
          onDone={() => {
            setReception(null);
            onChanged();
          }}
        />
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
};

// ── Skrzynka „Wysłane PUH" ────────────────────────────────────────────────────

const MONTHS_PL = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

// Kolor statusu wg StatusGlobalType (BE): doręczone / odrzucone / przyjęte / wysłane.
const puhStatusClass = (type?: number | null) => {
  switch (type) {
    case 40:
      return styles.statusOk;
    case 31:
      return styles.statusBad;
    case 30:
      return styles.statusInfo;
    default:
      return "";
  }
};

const PuhSentList = ({ searchText }: { searchText: string }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data, isLoading } = dispatchChartService.useGetPuhSent({
    Year: year,
    Month: month,
  });
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const items: IPuhSentItem[] = useMemo(() => {
    const all = data?.data?.Items ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    return all.filter((x) =>
      [x.ReceiverName, x.Title, x.ParcelNumber, x.ReceiverAddress, x.AuthorName]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q))
    );
  }, [data, searchText]);

  const shift = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;

  return (
    <div className={styles.content}>
      <div className={styles.listToolbar}>
        <div className={styles.toolbarInfo}>
          <button
            type="button"
            className={styles.pgBtn}
            onClick={() => shift(-1)}
            title="Poprzedni miesiąc"
          >
            ‹
          </button>
          <strong>
            {MONTHS_PL[month - 1]} {year}
          </strong>
          <button
            type="button"
            className={styles.pgBtn}
            onClick={() => shift(1)}
            disabled={isCurrent}
            title="Następny miesiąc"
          >
            ›
          </button>
          <span>Nadanych: {items.length}</span>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.empty}>Ładowanie…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          Brak przesyłek hybrydowych nadanych w tym miesiącu.
        </div>
      ) : (
        <div className={styles.table}>
          {items.map((it) => (
            <div
              key={it.Id}
              className={styles.row}
              title="Dwuklik — podgląd pisma"
              onDoubleClick={() => setPreview({ id: it.Id, title: it.Title })}
            >
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>
                  {it.ReceiverName ?? "—"}
                  {it.ParcelNumber && (
                    <span className={styles.sig}>{it.ParcelNumber}</span>
                  )}
                  {it.StatusName && (
                    <span
                      className={`${styles.statusBadge} ${puhStatusClass(
                        it.StatusType
                      )}`}
                    >
                      {it.StatusName}
                    </span>
                  )}
                </div>
                <div className={styles.rowSub}>
                  {it.Title ?? ""}
                  {it.ReceiverAddress ? ` · ${it.ReceiverAddress}` : ""}
                </div>
              </div>
              <div className={styles.rowMeta}>
                <div>
                  nadano {fmtDate(it.SendDate)}
                  {it.DeliveredDate
                    ? ` · doręczono ${fmtDate(it.DeliveredDate)}`
                    : ""}
                </div>
                <div className={styles.dim}>
                  {it.Registered ? "polecony" : "zwykły"}
                  {it.ProofOfDelivery ? " · ZPO" : ""}
                  {isTeamView && it.AuthorName ? ` · ${it.AuthorName}` : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default OutgoingView;
